import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { DeliveryZoneFormData, City } from '@/types/delivery-zone.types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface AddDeliveryZoneModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddDeliveryZoneModal({
  open,
  onClose,
  onSuccess,
}: AddDeliveryZoneModalProps) {
  const { post, get, loading } = useApi();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<DeliveryZoneFormData>({
    code: '',
    name: '',
    city_id: '',
    description: '',
  });
  const [cities, setCities] = useState<City[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // Fetch cities when modal opens
  useEffect(() => {
    if (open) {
      fetchCities();
    }
  }, [open]);

  const fetchCities = async () => {
    setCitiesLoading(true);
    try {
      const response: any = await get('/api/v1/settings/cities');
      setCities(response || []);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code || !formData.name || !formData.city_id) {
      toast({
        title: 'خطأ',
        description: 'يرجى ملء جميع الحقول المطلوبة',
        variant: 'destructive',
      });
      return;
    }

    try {
      await post('/api/v1/settings/delivery-zones', formData);
      toast({
        title: 'تمت الإضافة',
        description: 'تم إضافة منطقة التوصيل بنجاح',
      });
      onSuccess();
      handleClose();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في إضافة منطقة التوصيل',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setFormData({
      code: '',
      name: '',
      city_id: '',
      description: '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة منطقة توصيل جديدة</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="city">المدينة <span className="text-destructive">*</span></Label>
            <Select
              value={formData.city_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, city_id: value }))
              }
              disabled={citiesLoading}
            >
              <SelectTrigger id="city" className="bg-background">
                <SelectValue placeholder={citiesLoading ? 'جاري التحميل...' : 'اختر المدينة'} />
              </SelectTrigger>
              <SelectContent className="bg-background z-[9999]">
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name_ar || city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">رمز المنطقة <span className="text-destructive">*</span></Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, code: e.target.value }))
              }
              placeholder="ZONE-A"
              className="text-end"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">اسم المنطقة <span className="text-destructive">*</span></Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="منطقة أ"
              className="text-end"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">الوصف (اختياري)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="وصف المنطقة..."
              className="text-end resize-none"
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              إضافة
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
